import { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axios';
import { SERVER } from '../../../hooks/config';
import { MdVideoLibrary, MdDelete, MdPlayArrow } from 'react-icons/md';
import './UploadVideo.scss';

const UploadVideo = () => {
  const [videoFile, setVideoFile] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCurrentVideo();
  }, []);

  const fetchCurrentVideo = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/aboutus/getVideo');
      if (response.data.videoPath) {
        setCurrentVideo(response.data.videoPath);
      }
    } catch (error) {
      console.error('Error fetching video:', error);
      setError('Failed to fetch current video');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('video/')) {
        setError('Please select a video file');
        return;
      }
      
      // Check file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        setError('Video file must be less than 50MB');
        return;
      }
      
      setVideoFile(file);
      setError('');
      setMessage('');
    }
  };

  const handleUpload = async () => {
    if (!videoFile) {
      setError('Please select a video file first');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setMessage('');

      const formData = new FormData();
      formData.append('video', videoFile);

      const response = await axiosInstance.post('/aboutus/uploadVideo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage('Video uploaded successfully!');
      setVideoFile(null);
      document.getElementById('video-upload').value = '';
      fetchCurrentVideo(); // Refresh the current video
    } catch (error) {
      console.error('Error uploading video:', error);
      if (error.response?.status === 413) {
        setError('File too large. Maximum size is 50MB');
      } else {
        setError(error.response?.data?.error || 'Failed to upload video');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the current video?')) {
      return;
    }

    try {
      setLoading(true);
      // Since the backend always saves as aboutus.mp4, we need to implement a delete endpoint
      // For now, we'll use a workaround - upload an empty file or implement proper delete
      await axiosInstance.delete('/aboutus/deleteVideo');
      setMessage('Video deleted successfully!');
      setCurrentVideo(null);
    } catch (error) {
      console.error('Error deleting video:', error);
      setError(error.response?.data?.error || 'Failed to delete video');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="upload-video">
      <div className="video-header">
        <h2>About Us Video Management</h2>
        <p>Upload and manage the About Us page video</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {message && (
        <div className="success-message">
          {message}
        </div>
      )}

      <div className="video-upload-section">
        <div className="upload-card">
          <div className="upload-icon">
            <MdVideoLibrary />
          </div>
          <h3>Upload New Video</h3>
          <p>Select a video file to replace the current About Us video</p>
          
          <div className="file-input-container">
            <input
              type="file"
              id="video-upload"
              accept="video/*"
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="video-upload" className="file-input-label">
              Choose Video File
            </label>
            {videoFile && (
              <div className="file-info">
                <span>{videoFile.name}</span>
                <span>{(videoFile.size / (1024 * 1024)).toFixed(2)} MB</span>
              </div>
            )}
          </div>

          <button
            onClick={handleUpload}
            disabled={!videoFile || uploading}
            className="upload-btn"
          >
            {uploading ? 'Uploading...' : 'Upload Video'}
          </button>

          <div className="upload-note">
            <p>• Maximum file size: 50MB</p>
            <p>• Supported formats: MP4, MOV, AVI, etc.</p>
            <p>• Uploading will replace the current video</p>
          </div>
        </div>
      </div>

      <div className="current-video-section">
        <h3>Current Video</h3>
        
        {loading ? (
          <div className="loading">Loading video information...</div>
        ) : currentVideo ? (
          <div className="video-preview">
            <div className="video-container">
              <video controls>
                <source src={`${SERVER}${currentVideo}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="video-actions">
              <a
                href={`${SERVER}${currentVideo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="view-btn"
              >
                <MdPlayArrow />
                View Full Video
              </a>
              <button onClick={handleDelete} className="delete-btn">
                <MdDelete />
                Delete Video
              </button>
            </div>
          </div>
        ) : (
          <div className="no-video">
            <p>No video uploaded yet. Upload a video to display on the About Us page.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadVideo;